// Competitor Map types

export type CompetitorType = 'direct' | 'adjacent' | 'potential'
export type CompetitorTier = 'ai-first' | 'established'
export type PricingModel = 'seat' | 'usage' | 'flat' | 'freemium' | 'unknown'
export type ScrapeConfidence = 'low' | 'med' | 'high'
export type EnrichmentStatus = 'pending' | 'enriching' | 'enriched' | 'failed'

export type CompetitorColumn =
  | 'type'
  | 'url'
  | 'pricing'
  | 'whatTheyDo'
  | 'strengths'
  | 'gaps'
  | 'notes'
  | 'investors'
  | 'hq'
  | 'isAiFirst'

export const DEFAULT_VISIBLE_COLUMNS: Record<CompetitorColumn, boolean> = {
  type: true,
  url: true,
  pricing: true,
  whatTheyDo: true,
  strengths: true,
  gaps: true,
  notes: true,
  investors: false,
  hq: false,
  isAiFirst: true,
}

export interface Competitor {
  id: string
  projectId: string
  accountId: string
  name: string
  category?: string
  type: CompetitorType
  url?: string
  whatTheyDo?: string
  strengths?: string
  gaps?: string
  notes?: string
  investors?: string
  hq?: string
  isAiFirst: boolean
  tier: CompetitorTier
  suggestedByAi: boolean
  monitorEnabled: boolean
  monitorFields: string[]
  changeHistory: unknown[]
  headline?: string
  positioning?: string
  icpSignals?: string[]
  pricingModel?: PricingModel
  pricingTiers?: string[]
  keyCapabilities?: string[]
  notableGaps?: string[]
  logoUrl?: string
  pagesFetched?: string
  scrapedAt?: string
  scrapeConfidence?: ScrapeConfidence
  discoveryRationale?: string
  rawScrapeContent?: string
  lastCheckedAt?: string
  enrichmentStatus: EnrichmentStatus
  enrichmentError?: string
  enrichedAt?: string
  createdAt: string
  updatedAt: string
}

export interface CompetitorBriefContent {
  strengths: string[]
  weaknesses: string[]
  insights: string[]
  summary: string
  pricing?: string
}

export interface CompetitorBrief {
  id: string
  projectId: string
  accountId: string
  competitorId?: string
  category?: string
  content: CompetitorBriefContent
  generatedAt: string
  modelUsed?: string
  promptVersion: string
  createdAt: string
  updatedAt: string
}

export interface CompetitorColumnSettings {
  id: string
  projectId: string
  accountId: string
  visibleColumns: Record<string, boolean>
  createdAt: string
  updatedAt: string
}

export type BriefScope =
  | { type: 'competitor'; competitorId: string }
  | { type: 'category'; category: string }

// Jina multi-page scrape result
export interface JinaScrapeResult {
  name: string | null
  url: string
  headline: string | null
  positioning: string | null
  icpSignals: string[]
  pricingModel: PricingModel
  pricingTiers: string[] | null
  keyCapabilities: string[]
  notableGaps: string[]
  logoUrl: string | null
  hqLocation: string | null
  pagesFetched: string
  scrapedAt: string
  confidence: ScrapeConfidence
  rawMarkdown?: string
}

// AI competitor discovery
export interface CompetitorSuggestion {
  name: string
  url: string | null
  category: string
  type: CompetitorType
  whatTheyDo: string
  rationale: string
  confidence: ScrapeConfidence
}

export interface DiscoveryResult {
  suggestions: CompetitorSuggestion[]
  productContext: string
  generatedAt: string
}

// Batch scrape
export interface BatchScrapeJob {
  jobId: string
  total: number
  completed: number
  results: Array<{
    url: string
    status: 'pending' | 'scraping' | 'done' | 'error'
    result?: JinaScrapeResult
    error?: string
  }>
  startedAt: string
  completedAt?: string
}
